<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import NextButton from 'dashboard/components-next/button/Button.vue';
import PaymentSettingsAPI from 'dashboard/api/paymentSettings';
import QuotesAPI from 'dashboard/api/quotes';

const props = defineProps({
  quote: {
    type: Object,
    required: true,
  },
  isOpen: {
    type: Boolean,
    default: false,
  },
  editMode: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close', 'confirm']);

const { t } = useI18n();

// Payment settings from backend
const paymentSettings = ref({
  pix_key: '',
  pix_key_type: '',
  card_enabled: true,
  max_installments: 12,
  card_fees: {},
  debit_enabled: true,
  debit_fee: 0,
  cash_enabled: true,
  installments_with_fees: [],
});

const loadingSettings = ref(false);

// Payment state
const payments = ref([]);
const currentPayment = ref({
  payment_method: '',
  amount: 0,
  installments: 1,
  fee_percentage: 0,
  cash_received: null,
});

// UI state
const showAddPayment = ref(true);
const selectedCardType = ref(''); // 'credit' or 'debit'
const loadingPayments = ref(false);

// Computed
const totalQuote = computed(() => Number(props.quote?.total || 0));

const totalPaid = computed(() => {
  return payments.value.reduce((sum, p) => sum + Number(p.amount), 0);
});

const remainingAmount = computed(() => {
  const remaining = totalQuote.value - totalPaid.value;
  return remaining > 0 ? remaining : 0;
});

const changeAmount = computed(() => {
  if (currentPayment.value.payment_method !== 'cash') return 0;
  if (!currentPayment.value.cash_received) return 0;
  const change =
    Number(currentPayment.value.cash_received) - currentPayment.value.amount;
  return change > 0 ? change : 0;
});

const canAddPayment = computed(() => {
  if (!currentPayment.value.payment_method) return false;
  if (!currentPayment.value.amount || currentPayment.value.amount <= 0)
    return false;
  if (currentPayment.value.payment_method === 'cash') {
    if (
      currentPayment.value.cash_received &&
      currentPayment.value.cash_received < currentPayment.value.amount
    ) {
      return false;
    }
  }
  return true;
});

const canFinalize = computed(() => {
  return payments.value.length > 0 && remainingAmount.value === 0;
});

const availableInstallments = computed(() => {
  return paymentSettings.value.installments_with_fees || [];
});

// Methods
async function loadPaymentSettings() {
  loadingSettings.value = true;
  try {
    const response = await PaymentSettingsAPI.get();
    paymentSettings.value = response.data;
  } catch (error) {
    console.error('Error loading payment settings:', error);
  } finally {
    loadingSettings.value = false;
  }
}

async function loadExistingPayments() {
  if (!props.editMode || !props.quote?.id) return;

  loadingPayments.value = true;
  try {
    const response = await QuotesAPI.getPayments(props.quote.id);
    const existingPayments = response.data || [];

    // Map existing payments to our format
    payments.value = existingPayments.map(p => ({
      id: p.id,
      payment_method: p.payment_method,
      amount: Number(p.amount),
      installments: p.installments || 1,
      fee_percentage: Number(p.fee_percentage || 0),
      cash_received: p.cash_received ? Number(p.cash_received) : null,
    }));

    // If all paid, hide add payment form
    if (remainingAmount.value <= 0) {
      showAddPayment.value = false;
    }
  } catch (error) {
    console.error('Error loading existing payments:', error);
    payments.value = [];
  } finally {
    loadingPayments.value = false;
  }
}

function selectPaymentMethod(method) {
  currentPayment.value.payment_method = method;
  currentPayment.value.amount = remainingAmount.value;
  currentPayment.value.installments = 1;
  currentPayment.value.fee_percentage = 0;
  currentPayment.value.cash_received = null;
  selectedCardType.value = '';
}

function selectCardType(type) {
  selectedCardType.value = type;
  if (type === 'debit') {
    currentPayment.value.payment_method = 'debit_card';
    currentPayment.value.fee_percentage = paymentSettings.value.debit_fee || 0;
    currentPayment.value.installments = 1;
  } else {
    currentPayment.value.payment_method = 'credit_card';
    updateInstallmentFee();
  }
}

function updateInstallmentFee() {
  const installments = currentPayment.value.installments;
  const fees = paymentSettings.value.card_fees || {};
  currentPayment.value.fee_percentage = Number(fees[String(installments)] || 0);
}

function formatCurrency(value) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value || 0);
}

function getInstallmentLabel(installment) {
  const fees = paymentSettings.value.card_fees || {};
  const fee = Number(fees[String(installment)] || 0);
  const amount = currentPayment.value.amount || remainingAmount.value;
  const total = amount * (1 + fee / 100);
  const perInstallment = total / installment;

  if (fee > 0) {
    return `${installment}x ${formatCurrency(perInstallment)} (+${fee}%)`;
  }
  return `${installment}x ${formatCurrency(perInstallment)}`;
}

function addPayment() {
  if (!canAddPayment.value) return;

  const payment = {
    payment_method: currentPayment.value.payment_method,
    amount: Number(currentPayment.value.amount),
    installments: currentPayment.value.installments,
    fee_percentage: currentPayment.value.fee_percentage,
    cash_received: currentPayment.value.cash_received
      ? Number(currentPayment.value.cash_received)
      : null,
  };

  payments.value.push(payment);

  // Reset current payment
  currentPayment.value = {
    payment_method: '',
    amount: 0,
    installments: 1,
    fee_percentage: 0,
    cash_received: null,
  };
  selectedCardType.value = '';

  // If there's remaining amount, show add payment again
  if (remainingAmount.value > 0) {
    showAddPayment.value = true;
  } else {
    showAddPayment.value = false;
  }
}

function removePayment(index) {
  payments.value.splice(index, 1);
  showAddPayment.value = true;
}

function getPaymentMethodLabel(method) {
  const labels = {
    pix: 'PIX',
    credit_card: 'Cartao Credito',
    debit_card: 'Cartao Debito',
    cash: 'Dinheiro',
  };
  return labels[method] || method;
}

function getPaymentMethodIcon(method) {
  const icons = {
    pix: 'i-lucide-qr-code',
    credit_card: 'i-lucide-credit-card',
    debit_card: 'i-lucide-credit-card',
    cash: 'i-lucide-banknote',
  };
  return icons[method] || 'i-lucide-wallet';
}

function finalize() {
  emit('confirm', payments.value);
}

function close() {
  emit('close');
}

function reset() {
  payments.value = [];
  currentPayment.value = {
    payment_method: '',
    amount: 0,
    installments: 1,
    fee_percentage: 0,
    cash_received: null,
  };
  selectedCardType.value = '';
  showAddPayment.value = true;
}

// Watchers
watch(
  () => props.isOpen,
  async newVal => {
    if (newVal) {
      reset();
      await loadPaymentSettings();
      if (props.editMode) {
        await loadExistingPayments();
      }
    }
  }
);

watch(
  () => currentPayment.value.installments,
  () => {
    if (currentPayment.value.payment_method === 'credit_card') {
      updateInstallmentFee();
    }
  }
);

onMounted(async () => {
  if (props.isOpen) {
    await loadPaymentSettings();
    if (props.editMode) {
      await loadExistingPayments();
    }
  }
});
</script>

<template>
  <div
    v-if="isOpen"
    class="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
    @click.self="close"
  >
    <div
      class="bg-white dark:bg-slate-800 rounded-xl w-full max-w-lg shadow-2xl max-h-[90vh] overflow-hidden flex flex-col"
    >
      <!-- Header -->
      <div class="px-6 py-4 border-b border-slate-200 dark:border-slate-700">
        <div class="flex items-center justify-between">
          <div>
            <h3
              class="text-lg font-semibold text-slate-800 dark:text-slate-100"
            >
              {{ editMode ? 'Editar Pagamentos' : 'Fechamento de Venda' }}
            </h3>
            <p class="text-sm text-slate-500 mt-1">
              Pedido #{{ quote.id }} - {{ formatCurrency(totalQuote) }}
            </p>
          </div>
          <button
            class="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
            @click="close"
          >
            <span class="i-lucide-x text-xl text-slate-400" />
          </button>
        </div>
      </div>

      <!-- Content -->
      <div class="flex-1 overflow-auto p-6">
        <!-- Loading State -->
        <div v-if="loadingSettings" class="flex justify-center py-8">
          <span
            class="i-lucide-loader-2 animate-spin text-2xl text-slate-400"
          />
        </div>

        <template v-else>
          <!-- Payment Summary -->
          <div class="mb-6 p-4 bg-slate-50 dark:bg-slate-900/50 rounded-xl">
            <div class="flex justify-between items-center mb-2">
              <span class="text-sm text-slate-600 dark:text-slate-400">Total do Pedido</span>
              <span class="font-bold text-slate-800 dark:text-slate-100">{{
                formatCurrency(totalQuote)
              }}</span>
            </div>
            <div class="flex justify-between items-center mb-2">
              <span class="text-sm text-slate-600 dark:text-slate-400">Total Pago</span>
              <span class="font-semibold text-green-600">{{
                formatCurrency(totalPaid)
              }}</span>
            </div>
            <div
              class="flex justify-between items-center pt-2 border-t border-slate-200 dark:border-slate-700"
            >
              <span
                class="text-sm font-medium text-slate-700 dark:text-slate-300"
                >Restante</span>
              <span
                class="font-bold text-lg"
                :class="
                  remainingAmount > 0 ? 'text-orange-500' : 'text-green-500'
                "
              >
                {{ formatCurrency(remainingAmount) }}
              </span>
            </div>
          </div>

          <!-- Added Payments List -->
          <div v-if="payments.length > 0" class="mb-6">
            <h4
              class="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3"
            >
              Pagamentos Adicionados
            </h4>
            <div class="space-y-2">
              <div
                v-for="(payment, index) in payments"
                :key="index"
                class="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800"
              >
                <div class="flex items-center gap-3">
                  <span
                    :class="getPaymentMethodIcon(payment.payment_method)"
                    class="text-xl text-green-600"
                  />
                  <div>
                    <span
                      class="font-medium text-slate-800 dark:text-slate-200"
                    >
                      {{ getPaymentMethodLabel(payment.payment_method) }}
                    </span>
                    <span
                      v-if="payment.payment_method === 'credit_card'"
                      class="text-sm text-slate-500 ml-1"
                    >
                      ({{ payment.installments }}x)
                    </span>
                  </div>
                </div>
                <div class="flex items-center gap-3">
                  <span class="font-semibold text-green-600">{{
                    formatCurrency(payment.amount)
                  }}</span>
                  <button
                    class="p-1.5 hover:bg-red-100 dark:hover:bg-red-900/30 rounded transition-colors"
                    @click="removePayment(index)"
                  >
                    <span class="i-lucide-trash-2 text-red-500" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Add Payment Section -->
          <div v-if="showAddPayment && remainingAmount > 0">
            <h4
              class="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3"
            >
              {{
                payments.length > 0
                  ? 'Adicionar Outro Pagamento'
                  : 'Forma de Pagamento'
              }}
            </h4>

            <!-- Payment Method Selection -->
            <div
              v-if="!currentPayment.payment_method"
              class="grid grid-cols-3 gap-3 mb-4"
            >
              <!-- PIX -->
              <button
                v-if="paymentSettings.pix_key"
                class="flex flex-col items-center justify-center p-4 rounded-xl border-2 border-slate-200 dark:border-slate-600 hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all"
                @click="selectPaymentMethod('pix')"
              >
                <span class="i-lucide-qr-code text-3xl text-blue-500 mb-2" />
                <span class="font-medium text-slate-700 dark:text-slate-300">PIX</span>
              </button>

              <!-- Card -->
              <button
                v-if="
                  paymentSettings.card_enabled || paymentSettings.debit_enabled
                "
                class="flex flex-col items-center justify-center p-4 rounded-xl border-2 border-slate-200 dark:border-slate-600 hover:border-purple-500 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-all"
                @click="selectPaymentMethod('card')"
              >
                <span
                  class="i-lucide-credit-card text-3xl text-purple-500 mb-2"
                />
                <span class="font-medium text-slate-700 dark:text-slate-300">Cartao</span>
              </button>

              <!-- Cash -->
              <button
                v-if="paymentSettings.cash_enabled"
                class="flex flex-col items-center justify-center p-4 rounded-xl border-2 border-slate-200 dark:border-slate-600 hover:border-green-500 hover:bg-green-50 dark:hover:bg-green-900/20 transition-all"
                @click="selectPaymentMethod('cash')"
              >
                <span class="i-lucide-banknote text-3xl text-green-500 mb-2" />
                <span class="font-medium text-slate-700 dark:text-slate-300">Dinheiro</span>
              </button>
            </div>

            <!-- Card Type Selection -->
            <div
              v-if="
                currentPayment.payment_method === 'card' && !selectedCardType
              "
              class="grid grid-cols-2 gap-3 mb-4"
            >
              <button
                v-if="paymentSettings.debit_enabled"
                class="flex flex-col items-center justify-center p-4 rounded-xl border-2 border-slate-200 dark:border-slate-600 hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all"
                @click="selectCardType('debit')"
              >
                <span
                  class="i-lucide-credit-card text-3xl text-blue-500 mb-2"
                />
                <span class="font-medium text-slate-700 dark:text-slate-300">Debito</span>
                <span
                  v-if="paymentSettings.debit_fee > 0"
                  class="text-xs text-slate-500"
                >
                  {{ paymentSettings.debit_fee }}% taxa
                </span>
              </button>

              <button
                v-if="paymentSettings.card_enabled"
                class="flex flex-col items-center justify-center p-4 rounded-xl border-2 border-slate-200 dark:border-slate-600 hover:border-purple-500 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-all"
                @click="selectCardType('credit')"
              >
                <span
                  class="i-lucide-credit-card text-3xl text-purple-500 mb-2"
                />
                <span class="font-medium text-slate-700 dark:text-slate-300">Credito</span>
                <span class="text-xs text-slate-500">ate {{ paymentSettings.max_installments }}x</span>
              </button>
            </div>

            <!-- Payment Details Form -->
            <div
              v-if="
                currentPayment.payment_method &&
                currentPayment.payment_method !== 'card'
              "
              class="space-y-4"
            >
              <!-- Amount Input -->
              <div>
                <label
                  class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2"
                >
                  Valor
                </label>
                <div class="flex items-center gap-2">
                  <span
                    class="text-slate-400 dark:text-slate-500 text-lg font-medium shrink-0"
                    >R$</span>
                  <input
                    v-model.number="currentPayment.amount"
                    type="number"
                    step="0.01"
                    min="0"
                    :max="remainingAmount"
                    class="flex-1 px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-lg font-semibold focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <!-- Credit Card Installments -->
              <div v-if="currentPayment.payment_method === 'credit_card'">
                <label
                  class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2"
                >
                  Parcelas
                </label>
                <select
                  v-model="currentPayment.installments"
                  class="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 text-base focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option
                    v-for="inst in availableInstallments"
                    :key="inst.installments"
                    :value="inst.installments"
                    class="bg-white dark:bg-slate-800 py-2"
                  >
                    {{ getInstallmentLabel(inst.installments) }}
                  </option>
                </select>
                <p
                  v-if="currentPayment.fee_percentage > 0"
                  class="mt-2 text-sm text-orange-500"
                >
                  Taxa: {{ currentPayment.fee_percentage }}%
                </p>
              </div>

              <!-- Cash Received -->
              <div v-if="currentPayment.payment_method === 'cash'">
                <label
                  class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2"
                >
                  Valor Recebido (opcional)
                </label>
                <div class="flex items-center gap-2">
                  <span
                    class="text-slate-400 dark:text-slate-500 text-lg font-medium shrink-0"
                    >R$</span>
                  <input
                    v-model.number="currentPayment.cash_received"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Deixe vazio se exato"
                    class="flex-1 px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div
                  v-if="changeAmount > 0"
                  class="mt-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800"
                >
                  <div class="flex items-center justify-between">
                    <span
                      class="text-sm font-medium text-yellow-700 dark:text-yellow-400"
                      >Troco</span>
                    <span class="text-lg font-bold text-yellow-600">{{
                      formatCurrency(changeAmount)
                    }}</span>
                  </div>
                </div>
              </div>

              <!-- PIX Key Display -->
              <div
                v-if="
                  currentPayment.payment_method === 'pix' &&
                  paymentSettings.pix_key
                "
              >
                <div
                  class="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800"
                >
                  <p class="text-sm text-blue-700 dark:text-blue-400 mb-1">
                    Chave PIX:
                  </p>
                  <p
                    class="font-mono text-lg font-semibold text-blue-800 dark:text-blue-300"
                  >
                    {{ paymentSettings.pix_key }}
                  </p>
                  <p
                    class="text-xs text-blue-600 dark:text-blue-500 mt-1 capitalize"
                  >
                    Tipo: {{ paymentSettings.pix_key_type }}
                  </p>
                </div>
              </div>

              <!-- Action Buttons -->
              <div class="flex gap-3">
                <NextButton
                  faded
                  slate
                  class="flex-1"
                  label="Voltar"
                  icon="i-lucide-arrow-left"
                  @click="
                    currentPayment.payment_method = '';
                    selectedCardType = '';
                  "
                />
                <NextButton
                  :disabled="!canAddPayment"
                  class="flex-1"
                  blue
                  :label="
                    remainingAmount - currentPayment.amount <= 0
                      ? 'Adicionar'
                      : 'Adicionar Parcial'
                  "
                  icon="i-lucide-plus"
                  @click="addPayment"
                />
              </div>
            </div>
          </div>

          <!-- All Paid Message -->
          <div
            v-if="remainingAmount === 0 && payments.length > 0"
            class="text-center py-6"
          >
            <span
              class="i-lucide-check-circle text-5xl text-green-500 mb-3 block"
            />
            <p class="text-lg font-semibold text-green-600">
              Pagamento Completo!
            </p>
            <p class="text-sm text-slate-500 mt-1">
              Clique em finalizar para confirmar a venda.
            </p>
          </div>
        </template>
      </div>

      <!-- Footer -->
      <div
        class="px-6 py-4 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-3"
      >
        <NextButton faded slate label="Cancelar" @click="close" />
        <NextButton
          :disabled="!canFinalize"
          emerald
          :label="editMode ? 'Salvar Alteracoes' : 'Finalizar Venda'"
          icon="i-lucide-check"
          @click="finalize"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
select {
  appearance: none;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
  background-position: right 0.75rem center;
  background-repeat: no-repeat;
  background-size: 1.5em 1.5em;
  padding-right: 2.5rem;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
}

select option {
  padding: 8px 12px;
  white-space: normal;
  word-wrap: break-word;
}

.dark select option {
  background-color: #1e293b;
  color: #f1f5f9;
}
</style>
